import { useTour } from '@contexts/useTour/useTour';
import type { BadgeProps } from '@mantine/core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationDispatch } from '@domains/navigation';

/**
 * Single source of truth for the tutorial list shown in the account menu (desktop)
 * and in the mobile drawer. Replaces the standalone TourMenu popover that used to
 * live in the navbar (TV1-2299) — the status-badge semantics moved here verbatim so
 * both surfaces stay in sync.
 */

export type TourEntryBadge = {
  label: string;
  variant: BadgeProps['variant'];
  color: BadgeProps['color'];
};

export type TourEntry = {
  key: string;
  name: string;
  description: string;
  badge: TourEntryBadge;
};

type TourStatus = 'completed' | 'skipped' | 'incomplete' | 'start';

export function useTourEntries() {
  const { t } = useTranslation( 'tour' );
  const navigationDispatch = useNavigationDispatch();
  const { startTour, getTourStatus, tours } = useTour();

  const statusBadgeMap: Record<TourStatus, TourEntryBadge> = useMemo(() => ({
    completed: {
      label: t( 'completed' ),
      variant: 'filled',
      color: 'primary.6'
    },
    skipped: {
      label: t( 'skipped' ),
      variant: 'light',
      color: 'gray'
    },
    incomplete: {
      label: t( 'continue' ),
      variant: 'light',
      color: 'primary.6'
    },
    start: {
      label: t( 'start' ),
      variant: 'light',
      color: 'primary.6'
    }
  }), [t]);

  const entries: TourEntry[] = useMemo(() =>
    Object.values( tours ).map(( tour ) => {
      const status = getTourStatus( tour.key );
      return {
        key: tour.key,
        name: tour.name,
        description: tour.description,
        badge: ( status && statusBadgeMap[status]) || statusBadgeMap.start
      };
    }),
  [ tours, getTourStatus, statusBadgeMap ]);

  const startTourAndNavigate = useCallback(( tourKey: string ) => {
    const tour = Object.values( tours ).find(( item ) => item.key === tourKey );
    startTour( tourKey );
    if ( tour?.route ) {
      navigationDispatch(
        { type: 'navigation/request/navigate', payload: { path: tour.route }},
        { source: 'user' }
      );
    }
  }, [ tours, startTour, navigationDispatch ]);

  return { entries, startTour: startTourAndNavigate };
}
